import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

export type LegalSection = {
  id: string;
  heading: string;
  paragraphs?: string[];
  items?: string[];
};

const navItems = [
  { key: "privacy", label: "Privacy Policy", to: "/legal/privacy-policy", icon: "ri-lock-2-line" },
  { key: "terms", label: "Terms of Service", to: "/legal/terms-of-service", icon: "ri-file-list-3-line" },
  { key: "msa", label: "Master Service Agreement", to: "/legal/msa", icon: "ri-contract-line" },
];

type Props = {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  active: string;
};

export default function LegalLayout({ title, updated, intro, sections, active }: Props) {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-accent-100/60 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">Legal</span>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">{title}</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight tracking-tight">
              {title}
            </h1>
            <p className="mt-3 text-sm text-foreground-500">Effective: {updated}</p>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              {intro}
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Sidebar */}
              <aside className="lg:col-span-3">
                <div className="sticky top-28 space-y-6">
                  <nav className="rounded-xl border border-background-200 bg-background-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                      Legal documents
                    </p>
                    <ul className="flex flex-col gap-1">
                      {navItems.map((n) => (
                        <li key={n.key}>
                          <Link
                            to={n.to}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
                              active === n.key
                                ? "bg-primary-100 text-primary-700 font-medium"
                                : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                            }`}
                          >
                            <i className={`${n.icon} w-4 h-4 flex items-center justify-center shrink-0`} />
                            {n.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>

                  <div className="rounded-xl border border-background-200 bg-secondary-50 p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 font-heading mb-3">Questions?</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      If you have questions about these terms, reach out to our compliance team.
                    </p>
                    <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-mail-line text-primary-600 w-4 h-4 flex items-center justify-center mt-0.5 shrink-0" />
                        <a href="mailto:legal@voxmaster.com" className="hover:text-primary-600 cursor-pointer">legal@voxmaster.com</a>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-map-pin-2-line text-primary-600 w-4 h-4 flex items-center justify-center mt-0.5 shrink-0" />
                        <span className="leading-relaxed">
                          3960 Howard Hughes Parkway, Suite 500
                          <br />
                          Las Vegas, Nevada 89169
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </aside>

              {/* Document */}
              <div className="lg:col-span-9">
                <article className="space-y-10">
                  {sections.map((s, i) => (
                    <section key={s.id} id={s.id}>
                      <h2 className="text-lg md:text-xl font-semibold text-foreground-950 font-heading mb-3">
                        <span className="text-primary-600 mr-2">{String(i + 1).padStart(2, "0")}.</span>
                        {s.heading}
                      </h2>
                      {s.paragraphs?.map((p, pi) => (
                        <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                          {p}
                        </p>
                      ))}
                      {s.items && (
                        <ul className="space-y-2">
                          {s.items.map((it, ii) => (
                            <li key={ii} className="flex items-start gap-2.5 text-sm md:text-base text-foreground-700 leading-relaxed">
                              <i className="ri-check-line text-primary-600 w-4 h-4 flex items-center justify-center mt-1 shrink-0" />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  ))}
                </article>

                <div className="mt-14 p-6 rounded-xl border border-background-200 bg-background-100/60">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    This document is provided for informational purposes and may be updated from time to time. The
                    most current version always governs your use of Voxmaster services.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}